const _cfg = window.__quickBuyConfig || {};
const FEEDBACK_DURATION      = 2200; // desktop bar "Added to cart" display time
const LABEL_SELECT_SIZE      = _cfg.labelSelectSize        || 'Select size';
const LABEL_ADDED            = _cfg.labelAdded             || 'Added to cart';
const ENABLE_ADD_ANIMATION   = _cfg.enableAddAnimation     !== false;
const ENABLE_SHEET_ANIMATION = _cfg.enableSheetAnimation   !== false;

const MOBILE_MQ    = window.matchMedia('(max-width: 768px)');
const variantCache = new Map();

const overlay       = document.querySelector('.quickbuy-overlay');
const sheet         = document.querySelector('.quickbuy-sheet');
if (ENABLE_SHEET_ANIMATION && sheet) sheet.setAttribute('data-qb-animate', '');
const sheetName     = sheet?.querySelector('.quickbuy-sheet__name');
const sheetSizes    = sheet?.querySelector('.quickbuy-sheet__sizes');
const sheetFeedback = sheet?.querySelector('.quickbuy-sheet__feedback');
const sheetClose    = sheet?.querySelector('.quickbuy-sheet__close');

let feedbackTimer = null;
let isCartAdding  = false;

// ── API ────────────────────────────────────────────────────────────────────

async function fetchVariants(handle) {
  if (variantCache.has(handle)) return variantCache.get(handle);

  const token = theme.settings.storefrontApiToken;
  if (!token) return null;

  const query = `query QuickBuyVariants($handle: String!) {
    product(handle: $handle) {
      variants(first: 30) {
        nodes { id availableForSale selectedOptions { name value } }
      }
    }
  }`;

  try {
    const res  = await fetch(
      `https://${window.Shopify.shop}/api/2026-04/graphql.json`,
      {
        method: 'POST',
        headers: { 'X-Shopify-Storefront-Access-Token': token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, variables: { handle } }),
      }
    );
    const data = await res.json();
    const sizes = (data?.data?.product?.variants?.nodes ?? [])
      .map(v => {
        const opt = v.selectedOptions.find(o => o.name.toLowerCase() === 'size');
        return opt ? { numericId: v.id.split('/').pop(), size: opt.value, available: v.availableForSale } : null;
      })
      .filter(Boolean);

    variantCache.set(handle, sizes);
    return sizes;
  } catch (err) {
    console.error('[quickbuy] fetch failed:', err);
    return null;
  }
}

async function addToCart(numericId) {
  const res  = await fetch(theme.routes.cartAdd, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'X-Requested-With': 'XMLHttpRequest' },
    body: `id=${numericId}&quantity=1`,
  });
  const data = await res.json();
  if (data.status === 422) throw new Error(data.description);
  document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data } }));
}

// ── Helpers ────────────────────────────────────────────────────────────────

const buildSizeButtons = sizes => sizes.map(v => {
  const s = !v.available;
  return `<button class="quickbuy-size-btn${s ? ' is-sold-out' : ''}" data-numeric-id="${v.numericId}" type="button"${s ? ' aria-disabled="true"' : ''}>${v.size}</button>`;
}).join('');

const lockButtons   = c => c.querySelectorAll('.quickbuy-size-btn').forEach(b => { b.disabled = true; });
const unlockButtons = c => c.querySelectorAll('.quickbuy-size-btn:not(.is-sold-out)').forEach(b => { b.disabled = false; });

// force=true bypasses the isCartAdding lock (used by cart:markup-built)
function closeAllBars(force = false) {
  if (isCartAdding && !force) return;
  document.querySelectorAll('.grid__item.qb-is-open').forEach(c => c.classList.remove('qb-is-open'));
}

function closeSheet(force = false) {
  if (isCartAdding && !force) return;
  if (ENABLE_SHEET_ANIMATION && sheet?.classList.contains('is-open')) {
    sheet.classList.replace('is-open', 'qb-closing') ||
      (sheet.classList.remove('is-open'), sheet.classList.add('qb-closing'));
    setTimeout(() => {
      sheet.classList.remove('qb-closing');
      overlay?.classList.remove('is-visible');
      document.body.style.overflow = '';
    }, 250); // matches qb-sheet-out animation duration
  } else {
    overlay?.classList.remove('is-visible');
    sheet?.classList.remove('is-open', 'qb-closing');
    document.body.style.overflow = '';
  }
}

function setupSheetSizeButtons() {
  sheetSizes?.querySelectorAll('.quickbuy-size-btn:not(.is-sold-out)').forEach(btn => {
    btn.addEventListener('click', async () => {
      lockButtons(sheetSizes);
      isCartAdding = true;
      sheetSizes.querySelectorAll('.quickbuy-size-btn').forEach(b => b.classList.remove('is-selected'));
      btn.classList.add('is-selected');
      if (ENABLE_ADD_ANIMATION) btn.classList.add('is-adding');
      try {
        await addToCart(btn.dataset.numericId);
        // Close immediately — synchronized with the cart drawer starting to open.
        // ajaxProduct:added was just dispatched inside addToCart(), so the drawer
        // begins its slide-in at exactly this moment. We match that with our slide-out.
        isCartAdding = false;
        clearTimeout(feedbackTimer);
        btn.classList.remove('is-adding', 'is-selected');
        if (sheetFeedback) { sheetFeedback.textContent = ''; sheetFeedback.hidden = true; }
        closeSheet();
      } catch {
        isCartAdding = false;
        btn.classList.remove('is-selected', 'is-adding');
        unlockButtons(sheetSizes);
      }
    });
  });
}

overlay?.addEventListener('click', closeSheet);
sheetClose?.addEventListener('click', closeSheet);

document.addEventListener('click', e => {
  if (isCartAdding) return;
  if (!e.target.closest('.grid__item') && !e.target.closest('.quickbuy-sheet')) closeAllBars();
});

document.addEventListener('cart:markup-built', () => {
  isCartAdding = false;
  clearTimeout(feedbackTimer);
  closeAllBars(true);
  closeSheet(true);
});

// ── Web Component ──────────────────────────────────────────────────────────

class QuickBuyTrigger extends HTMLElement {
  connectedCallback() {
    // Escape <a class="grid__item-image-wrapper"> — Depict hides these with
    // display:none on colour switch, which would take us with it.
    const imageWrapper = this.closest('.grid__item-image-wrapper');
    if (imageWrapper) {
      const content = imageWrapper.parentElement;
      if (content) {
        if (getComputedStyle(content).position === 'static') content.style.position = 'relative';
        const meta = content.querySelector('.grid-product__meta');
        meta ? content.insertBefore(this, meta) : content.appendChild(this);
      }
      return; // connectedCallback fires again from the new position
    }

    if (this._initialized) return;
    this._initialized = true;

    this.card = this.closest('.grid__item');
    if (!this.card) return;

    this._activeHandle = this.getActiveHandle();

    // ── Height sync ────────────────────────────────────────────
    const syncHeight = () => requestAnimationFrame(() => {
      const w = this.card.querySelector('.grid__item-image-wrapper:not(.hidden)');
      if (w?.offsetHeight) this.style.height = w.offsetHeight + 'px';
    });
    syncHeight();
    new ResizeObserver(syncHeight).observe(
      this.card.querySelector('.grid__item-image-wrapper') || this.card
    );

    // ── Hover tracking ──────────────────────────────────────────
    // Host is outside <a> so CSS :hover can't reach the button — track via JS.
    const imgWrappers = () => Array.from(this.card.querySelectorAll('.grid__item-image-wrapper'));
    const activeMask  = () => this.card.querySelector('.grid__item-image-wrapper:not(.hidden) .grid-product__image-mask');

    const clearHover = () => {
      this.classList.remove('qb-img-hover');
      this.card.querySelectorAll('.grid-product__image-mask').forEach(m => m.classList.remove('qb-mask-hover'));
    };

    const showBtn = () => {
      this.classList.add('qb-img-hover');
      activeMask()?.classList.add('qb-mask-hover');
      fetchVariants(this.getActiveHandle()); // prefetch on hover
    };

    const hideBtn = e => {
      const to = e.relatedTarget;
      if (!imgWrappers().some(w => w === to || w.contains(to)) && this !== to && !this.contains(to)) {
        clearHover();
      }
    };

    const attachHover = () => imgWrappers().forEach(w => {
      w.removeEventListener('mouseenter', showBtn);
      w.removeEventListener('mouseleave', hideBtn);
      w.addEventListener('mouseenter', showBtn);
      w.addEventListener('mouseleave', hideBtn);
    });
    attachHover();

    this.addEventListener('mouseleave', e => {
      if (!imgWrappers().some(w => w === e.relatedTarget || w.contains(e.relatedTarget))) clearHover();
    });

    // ── Mutation observer ───────────────────────────────────────
    new MutationObserver(mutations => {
      syncHeight();
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.classList?.contains('grid__item-image-wrapper')) { attachHover(); break; }
        }
      }
      const h = this.getActiveHandle();
      if (h !== this._activeHandle) {
        this._activeHandle = h;
        fetchVariants(h);
        if (this.card.classList.contains('qb-is-open') && !MOBILE_MQ.matches) this._rerenderBar();
      }
    }).observe(this.card, { attributes: true, attributeFilter: ['class'], childList: true, subtree: true });

    // ── Render ──────────────────────────────────────────────────
    this.innerHTML = `
      <button class="quickbuy-trigger" type="button" aria-label="Quick add to cart"><span class="quickbuy-trigger__dot"></span></button>
      <div class="quickbuy-bar">
        <div class="quickbuy-bar__header">
          <span class="quickbuy-bar__label">${LABEL_SELECT_SIZE}</span>
          <button class="quickbuy-bar__close" type="button" aria-label="Close"></button>
        </div>
        <div class="quickbuy-bar__sizes"></div>
        <div class="quickbuy-bar__feedback" aria-live="polite"></div>
      </div>`;

    this.trigger     = this.querySelector('.quickbuy-trigger');
    this.bar         = this.querySelector('.quickbuy-bar');
    this.barSizes    = this.querySelector('.quickbuy-bar__sizes');
    this.barFeedback = this.querySelector('.quickbuy-bar__feedback');
    this.barClose    = this.querySelector('.quickbuy-bar__close');

    this.bar.addEventListener('click', e => e.stopPropagation());
    this.trigger.addEventListener('click', e => {
      e.stopPropagation();
      MOBILE_MQ.matches ? this.openSheet() : this.openBar();
    });
    this.barClose.addEventListener('click', e => {
      e.stopPropagation();
      if (!isCartAdding) this.card.classList.remove('qb-is-open');
    });
  }

  getActiveHandle() {
    const w = this.card.querySelector('.grid__item-image-wrapper:not(.hidden)');
    if (w?.href) {
      const h = new URL(w.href).pathname.split('/products/')[1];
      if (h) return h;
    }
    return this.dataset.productHandle;
  }

  getActiveTitle() {
    return this.card.querySelector('a.grid-product__title')?.textContent?.trim()
      || this.dataset.productTitle
      || '';
  }

  // ── Bar (desktop) ─────────────────────────────────────────────

  async openBar() {
    if (isCartAdding) return;
    const wasOpen = this.card.classList.contains('qb-is-open');
    closeAllBars();
    if (wasOpen) return;

    this.card.classList.add('qb-is-open');
    this.barFeedback.textContent = '';

    const handle = this.getActiveHandle();
    if (!variantCache.has(handle)) {
      this.barSizes.innerHTML = '<span class="quickbuy-bar__loading"></span>';
    }
    const sizes = await fetchVariants(handle);
    if (!sizes?.length) { this.card.classList.remove('qb-is-open'); return; }
    this.barSizes.innerHTML = buildSizeButtons(sizes);
    this.bindBarSizeButtons();
  }

  async _rerenderBar() {
    this.barSizes.innerHTML = '<span class="quickbuy-bar__loading"></span>';
    this.barFeedback.textContent = '';
    const sizes = await fetchVariants(this.getActiveHandle());
    if (!sizes?.length) { this.card.classList.remove('qb-is-open'); return; }
    this.barSizes.innerHTML = buildSizeButtons(sizes);
    this.bindBarSizeButtons();
  }

  bindBarSizeButtons() {
    this.barSizes.querySelectorAll('.quickbuy-size-btn:not(.is-sold-out)').forEach(btn => {
      btn.addEventListener('click', async e => {
        e.stopPropagation();
        lockButtons(this.barSizes);
        isCartAdding = true;
        this.barSizes.querySelectorAll('.quickbuy-size-btn').forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        if (ENABLE_ADD_ANIMATION) btn.classList.add('is-adding');
        try {
          await addToCart(btn.dataset.numericId);
          btn.classList.remove('is-adding');
          this.barFeedback.textContent = LABEL_ADDED;
          clearTimeout(feedbackTimer);
          feedbackTimer = setTimeout(() => {
            isCartAdding = false;
            this.barFeedback.textContent = '';
            btn.classList.remove('is-selected');
            this.card.classList.remove('qb-is-open');
          }, FEEDBACK_DURATION);
        } catch {
          isCartAdding = false;
          btn.classList.remove('is-selected', 'is-adding');
          unlockButtons(this.barSizes);
        }
      });
    });
  }

  // ── Sheet (mobile) ────────────────────────────────────────────

  async openSheet() {
    overlay?.classList.add('is-visible');
    document.body.style.overflow = 'hidden';

    const sizes = await fetchVariants(this.getActiveHandle());
    if (!sizes?.length) { closeSheet(); return; }

    if (sheetName) sheetName.textContent = `${this.getActiveTitle()} — ${LABEL_SELECT_SIZE}`;
    if (sheetFeedback) { sheetFeedback.textContent = ''; sheetFeedback.hidden = true; }
    this._renderSheet(sizes);
    sheet?.classList.add('is-open');
  }

  _renderSheet(sizes) {
    if (!sheetSizes) return;
    const row1 = sizes.slice(0, 4);
    const row2 = sizes.slice(4, 8);
    sheetSizes.innerHTML =
      `<div class="quickbuy-sheet__row">${buildSizeButtons(row1)}</div>` +
      (row2.length ? `<div class="quickbuy-sheet__row">${buildSizeButtons(row2)}</div>` : '');
    setupSheetSizeButtons();
  }
}

customElements.define('quick-buy-trigger', QuickBuyTrigger);
